/**
 * PWA auto-update - every chain29 app (POS, KDS, kiosk, expo, display, time clock,
 * owner / admin, order, back office) is one build, so one module keeps them all on
 * the deployed version.
 *
 * How a deploy reaches a screen that is already open:
 *  1. The build writes /version.json and stamps the same id into service-worker.js
 *     (frontend/scripts/stamp-build.js), so every deploy is a new worker.
 *  2. Every 5 minutes, when the screen wakes / comes back online, we read
 *     /version.json (no cache) and ask the browser to check the worker.
 *  3. A different build = "update ready". It is applied (a reload) only when it is
 *     safe: nothing holds it (a cart with items, a payment dialog, an open check -
 *     see holdUpdate / useUpdateHold) and
 *       - on the floor / counter screens (POS, KDS, kiosk, expo, display, dining,
 *         time clock): when the screen is hidden or untouched for 2 minutes;
 *       - everywhere else (back office, admin, storefront): when the tab is hidden;
 *     until then the "Update ready" bar offers a Reload button.
 *
 * The build id comes from REACT_APP_BUILD_SHA (DO: ${_self.COMMIT_HASH}); without
 * one (local dev) the first /version.json read is the baseline.
 */
import { useEffect, useState } from 'react';

export const CHECK_EVERY_MS = 5 * 60 * 1000;
export const IDLE_MS = 2 * 60 * 1000;
const VERSION_URL = '/version.json';

const EMBEDDED = (process.env.REACT_APP_BUILD_SHA || '').trim() || null;

const state = {
  running: EMBEDDED,          // build this page runs
  available: null,            // newer build seen on the server
  ready: false,
  holds: new Set(),
  lastInput: Date.now(),
  started: false,
  reloading: false,
};
const listeners = new Set();
const emit = () => listeners.forEach((fn) => { try { fn(snapshot()); } catch (_) { /* listener */ } });
const snapshot = () => ({
  ready: state.ready,
  running: state.running,
  available: state.available,
  held: state.holds.size > 0,
});

export function getUpdateState() { return snapshot(); }
export function subscribeUpdate(fn) { listeners.add(fn); return () => listeners.delete(fn); }

/** Hold the update while `key` is busy (a payment in flight, a cart with items). */
export function holdUpdate(key, active) {
  const had = state.holds.size;
  if (active) state.holds.add(key); else state.holds.delete(key);
  if (had !== state.holds.size) { emit(); maybeApply(); }
}

/** React: hold the update while `active` is true (released on unmount). */
export function useUpdateHold(key, active) {
  useEffect(() => {
    holdUpdate(key, !!active);
    return () => holdUpdate(key, false);
  }, [key, active]);
}

/** The floor / counter screens that may reload on their own when idle. */
export function isUnattendedScreen(loc = window.location) {
  const first = (loc.hostname || '').split('.')[0];
  if (['pos', 'kds', 'kiosk', 'expo', 'display', 'timeclock', 'checkin'].includes(first)) return true;
  return /^\/(pos|kds|kiosk|expo|display|dining|timeclock|time-clock|pwa)(\/|$)/.test(loc.pathname || '');
}

function markReady(build) {
  if (build) state.available = build;
  if (!state.ready) { state.ready = true; emit(); }
  maybeApply();
}

export function applyUpdate({ force = false } = {}) {
  if (state.reloading) return false;
  if (!force && state.holds.size > 0) return false;
  state.reloading = true;
  const reg = window.__c29SwRegistration;
  if (reg && reg.waiting) {
    try { reg.waiting.postMessage({ type: 'SKIP_WAITING' }); } catch (_) { /* reload anyway */ }
  }
  window.location.reload();
  return true;
}

function maybeApply() {
  if (!state.ready || state.reloading || state.holds.size > 0) return;
  const hidden = document.visibilityState === 'hidden';
  const idle = Date.now() - state.lastInput >= IDLE_MS;
  if (hidden || (isUnattendedScreen() && idle)) applyUpdate();
}

export async function checkForUpdate() {
  try {
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    const build = data && data.build ? String(data.build) : null;
    if (!build) return null;
    if (!state.running) { state.running = build; emit(); }
    else if (build !== state.running) markReady(build);
    const reg = window.__c29SwRegistration;
    if (reg && typeof reg.update === 'function') reg.update().catch(() => {});
    return build;
  } catch (_) {
    return null; // offline: try again later
  }
}

/** Start once, from index.js. Safe to call more than once. */
export function startAutoUpdate() {
  if (state.started || typeof window === 'undefined') return;
  state.started = true;
  // read-only state + hold, for support and the e2e spec (tests/e2e/pwa_update.spec.js)
  window.__c29Update = { state: getUpdateState, hold: holdUpdate, check: checkForUpdate };

  const touch = () => { state.lastInput = Date.now(); };
  ['pointerdown', 'keydown', 'touchstart', 'wheel'].forEach((ev) =>
    window.addEventListener(ev, touch, { passive: true, capture: true }));

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkForUpdate(); else maybeApply();
  });
  window.addEventListener('online', () => checkForUpdate());

  if ('serviceWorker' in navigator) {
    // A new worker took over this page (it skips waiting): the page still runs the
    // old bundle - the update is ready. No controller before = first install, not an update.
    let hadController = !!navigator.serviceWorker.controller;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (hadController) markReady(null);
      hadController = true;
    });
  }

  checkForUpdate();
  setInterval(checkForUpdate, CHECK_EVERY_MS);
  setInterval(maybeApply, 15 * 1000);
}

/** React: the update state, re-rendered on change. */
export function useAutoUpdate() {
  const [s, setS] = useState(snapshot);
  useEffect(() => subscribeUpdate(setS), []);
  return s;
}
